<template>
  <div
    style="min-height: var(--app-viewport-height, 100vh); background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 100%);"
  >
    <Loader :loading="showLoader" />

    <v-container fluid class="py-4 py-md-6">
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <v-sheet color="transparent">
            <v-card
              rounded="xl"
              elevation="3"
              class="px-3 px-md-6 py-3 d-flex align-center"
              style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);"
            >
              <v-btn icon variant="text" @click="goBack">
                <v-icon size="28" color="white">mdi-arrow-left-circle</v-icon>
              </v-btn>

              <div class="ml-2">
                <div
                  class="text-body-1 text-md-h6 font-weight-semibold"
                  style="color: #ffffff;"
                >
                  {{ $t('incidents.title') }}
                </div>
                <div
                  class="text-caption text-md-body-2"
                  style="color: rgba(255,255,255,0.85);"
                >
                  {{ $t('incidents.send-incident') }}
                </div>
              </div>

              <v-spacer />

              <v-btn
                icon
                variant="text"
                color="white"
                :loading="isRefreshing"
                @click="refreshData"
              >
                <v-icon size="24">mdi-refresh</v-icon>
              </v-btn>
            </v-card>
          </v-sheet>

          <!-- Premium Feature Info Card -->
          <v-sheet color="transparent" class="mt-4">
            <v-card
              rounded="xl"
              elevation="2"
              class="pa-4 premium-info-card"
            >
              <div class="d-flex align-start ga-3">
                <v-avatar
                  color="#DC3545"
                  size="48"
                  variant="tonal"
                  class="flex-shrink-0"
                >
                  <v-icon size="24" color="#DC3545">mdi-star</v-icon>
                </v-avatar>

                <div class="flex-grow-1">
                  <div class="d-flex align-center mb-2" style="flex-wrap: wrap; gap: 6px;">
                    <v-chip
                      color="#DC3545"
                      size="small"
                      variant="flat"
                      class="text-uppercase font-weight-bold mr-2"
                       style="letter-spacing: 0.5px; padding: 0 10px; border-radius: 20px;"
                    >
                      {{ $t('incidents.premium-feature') }}
                    </v-chip>
                    <span class="text-subtitle-2 font-weight-semibold" style="color: #001437;">
                      {{ $t('incidents.custom-service-provider-assignment') }}
                    </span>
                </div>

                <p class="text-body-2 mb-0" style="color: #4E5965; line-height: 1.6;">
                  <v-icon size="16" color="#9AA6B1" class="mr-1">mdi-information-outline</v-icon>
                  <span v-html="$t('incidents.basic-version-note')"></span>
                  <span v-html="$t('incidents.premium-upgrade-message')"></span>
                </p>
              </div>

                <v-btn
                  variant="tonal"
                  color="#DC3545"
                  size="small"
                  rounded="lg"
                  class="text-none flex-shrink-0 d-none d-md-flex"
                  disabled
                >
                  <v-icon start size="18">mdi-arrow-up-circle</v-icon>
                  Upgrade
                </v-btn>
              </div>
            </v-card>
          </v-sheet>

          <v-sheet color="transparent" class="mt-4">
            <v-card
              color="white"
              elevation="2"
              rounded="xl"
              class="pa-3 pa-md-5"
            >
              <!-- Status Summary Chips -->
              <v-row class="mb-3" align="center" no-gutters>
                <v-col cols="12" class="d-flex flex-wrap ga-2">
                  <v-chip
                    variant="tonal"
                    color="#DC3545"
                    size="small"
                    label
                    prepend-icon="mdi-alert-circle"
                  >
                    {{ $t('incidents.incident-open') }} • {{ statusSummary.open }}
                  </v-chip>

                  <v-chip
                    variant="tonal"
                    color="#28A745"
                    size="small"
                    label
                    prepend-icon="mdi-check-circle"
                  >
                    {{ $t('office.done') }} • {{ statusSummary.done }}
                  </v-chip>

                  <v-spacer />

                  <span
                    v-if="filteredIncidents.length"
                    class="text-caption text-md-body-2"
                    style="color: #718096;"
                  >
                    Showing {{ filteredIncidents.length }} of
                    {{ incidents.length }} {{ $t('incidents.title').toLowerCase() }}
                  </span>
                </v-col>
              </v-row>

              <!-- Search + Filter + Sort -->
              <v-row align="center" class="mb-4" no-gutters>
                <v-col cols="12" sm="6" md="4" class="mb-3 mb-sm-0 pr-sm-3">
                  <v-text-field
                    v-model="searchQuery"
                    :placeholder="$t('office.search') + '...'"
                    prepend-inner-icon="mdi-magnify"
                    variant="solo-filled"
                    density="comfortable"
                    hide-details
                    clearable
                    rounded="lg"
                    base-color="#9AA6B1"
                    color="#23A5E6"
                  />
                </v-col>

                <v-spacer />

                <v-col cols="auto" class="d-flex flex-wrap ga-2">
                  <v-btn
                    :variant="showFilters ? 'flat' : 'outlined'"
                    :color="showFilters ? '#285096' : '#4E5965'"
                    rounded="lg"
                    size="default"
                    class="text-none"
                    @click="showFilters = !showFilters"
                  >
                    <v-icon start size="20">mdi-filter-variant</v-icon>
                    <span class="d-none d-sm-inline">{{ $t('navigation-bar.filter') }}</span>
                    <v-chip
                      v-if="activeFilterCount > 0"
                      size="x-small"
                      color="#23A5E6"
                      class="ml-2"
                      label
                    >
                      {{ activeFilterCount }}
                    </v-chip>
                  </v-btn>

                  <v-menu location="bottom end">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        variant="outlined"
                        color="#4E5965"
                        rounded="lg"
                        size="default"
                        class="text-none"
                      >
                        <v-icon start size="20">mdi-sort</v-icon>
                        <span class="d-none d-sm-inline">
                          {{ currentSortLabel }}
                        </span>
                        <v-icon end size="18">mdi-chevron-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list density="compact" rounded="lg" elevation="3" min-width="220">
                      <v-list-subheader class="text-caption">
                        {{ $t('navigation-bar.sort') }}
                      </v-list-subheader>
                      <v-list-item
                        v-for="option in sortOptions"
                        :key="option.value"
                        :active="currentSort === option.value"
                        active-color="#285096"
                        rounded="lg"
                        @click="currentSort = option.value"
                      >
                        <v-list-item-title>
                          {{ option.label }}
                        </v-list-item-title>
                        <template #append>
                          <v-icon
                            v-if="currentSort === option.value"
                            size="18"
                            color="#23A5E6"
                          >
                            mdi-check
                          </v-icon>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>

              <!-- Quick Status Filter -->
              <v-row class="mb-4" no-gutters>
                <v-col cols="12" class="d-flex flex-wrap ga-2">
                  <span
                    class="text-caption text-uppercase"
                    style="color: #A0AEC0; letter-spacing: 0.08em;"
                  >
                    {{ $t('incidents.incident-status') }}
                  </span>
                  <v-chip-group
                    v-model="filters.status"
                    selected-class=""
                    class="ml-4"
                    column
                  >
                    <v-chip value="all" label size="small" variant="outlined" color="#4E5965" class="text-none">
                      {{ $t('booking.all') }}
                    </v-chip>
                    <v-chip value="open" label size="small" variant="tonal" color="#DC3545" class="text-none">
                      {{ $t('incidents.incident-open') }}
                    </v-chip>
                    <v-chip value="done" label size="small" variant="tonal" color="#28A745" class="text-none">
                      {{ $t('office.done') }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>

              <!-- Expanded Filters -->
              <v-expand-transition>
                <v-card
                  v-if="showFilters"
                  rounded="xl"
                  class="mb-4 pa-4 pa-md-5"
                  elevation="0"
                  style="background: linear-gradient(135deg, #F3F6FF 0%, #FFFFFF 100%); border: 1px solid #E2E8F0;"
                >
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.timeRange"
                        :items="timeRangeOptions"
                        item-title="label"
                        item-value="value"
                        :label="$t('occupancy.time-range')"
                        variant="solo-filled"
                        density="comfortable"
                        hide-details
                        rounded="lg"
                        prepend-inner-icon="mdi-calendar-range"
                        color="#23A5E6"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.category"
                        :items="categoryOptions"
                        item-title="name"
                        item-value="id"
                        :label="$t('incidents.incident-category')"
                        variant="solo-filled"
                        density="comfortable"
                        hide-details
                        rounded="lg"
                        prepend-inner-icon="mdi-tag-outline"
                        color="#23A5E6"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.floor"
                        :items="floorOptions"
                        item-title="name"
                        item-value="id"
                        :label="$t('office.floor')"
                        variant="solo-filled"
                        density="comfortable"
                        hide-details
                        rounded="lg"
                        prepend-inner-icon="mdi-stairs"
                        color="#23A5E6"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.status"
                        :items="statusFilterOptions"
                        item-title="label"
                        item-value="value"
                        :label="$t('incidents.incident-status')"
                        variant="solo-filled"
                        density="comfortable"
                        hide-details
                        rounded="lg"
                        prepend-inner-icon="mdi-flag-outline"
                        color="#23A5E6"
                      />
                    </v-col>
                  </v-row>

                  <div v-if="hasActiveFilters" class="d-flex justify-end mt-3">
                    <v-btn variant="text" color="#e60664" size="small" class="text-none" @click="clearFilters">
                      <v-icon start size="18">mdi-close-circle</v-icon>
                      {{ $t('users.reset') }}
                    </v-btn>
                  </div>
                </v-card>
              </v-expand-transition>

              <!-- Error -->
              <v-alert
                v-if="errorMessage"
                type="error"
                variant="tonal"
                rounded="xl"
                class="mb-4"
                closable
                @click:close="errorMessage = null"
              >
                <template #title>{{ $t('incidents.loading-error-data') }}</template>
                {{ errorMessage }}
                <template #append>
                  <v-btn variant="text" color="error" class="text-none" @click="refreshData">
                    {{ $t('incidents.refreshing') }}
                  </v-btn>
                </template>
              </v-alert>

              <!-- Empty State -->
              <v-card
                v-else-if="filteredIncidents.length === 0 && !isLoading"
                variant="outlined"
                rounded="xl"
                class="pa-8 text-center"
              >
                <v-icon size="72" color="#B7BFC7">mdi-clipboard-text-off-outline</v-icon>
                <h3 class="text-h6 mt-4" style="color: #373F47;">
                  {{ $t('incidents.incident-not-found') }}
                </h3>
                <p class="text-body-2 mt-2" style="color: #9AA6B1;">
                  {{ hasActiveFilters ? $t('booking.apply-filter') : $t('incidents.incident-not-found') }}
                </p>
              </v-card>

              <!-- Desktop Table -->
              <v-card v-else variant="flat" rounded="xl" class="d-none d-md-block">
                <v-data-table
                  :headers="tableHeaders"
                  :items="filteredIncidents"
                  :items-per-page="10"
                  hover
                  density="comfortable"
                  :header-color="'#EDF3FD'"
                  class="responsive-table"
                  @click:row="(e, { item }) => openIncidentDialog(item)"
                >
                  <template #[`item.incidentNumber`]="{ item }">
                    <span class="font-weight-medium text-no-wrap">#{{ item.serviceNumber || item.incidentNumber || '-' }}</span>
                  </template>

                  <template #[`item.serviceCategoryName`]="{ item }">
                    <div class="d-flex align-center">
                      <v-avatar color="#EDF3FD" size="28" class="mr-2 flex-shrink-0">
                        <v-icon size="14" color="#285096">{{ getCategoryIcon(item.serviceCategoryName) }}</v-icon>
                      </v-avatar>
                      <span class="font-weight-medium" style="color:#001437; word-break: break-word; line-height: 1.3;">
                        {{ item.serviceCategoryName || $t('occupancy.all-categories') }}
                      </span>
                    </div>
                  </template>

                  <template #[`item.description`]="{ value }">
                    <span class="d-inline-block" style="word-break: break-word; line-height: 1.3;">{{ value }}</span>
                  </template>

                  <template #[`item.location`]="{ item }">
                    <div class="d-flex align-center ga-1">
                      <v-icon size="14" color="#9AA6B1" class="flex-shrink-0">mdi-map-marker</v-icon>
                      <span style="word-break: break-word; line-height: 1.3;">{{ getLocationDisplay(item) }}</span>
                    </div>
                  </template>

                  <template #[`item.floorName`]="{ item }">
                    <span class="text-no-wrap">{{ getFloorDisplay(item) }}</span>
                  </template>

                  <template #[`item.creationTime`]="{ value }">
                    <span class="text-no-wrap" style="font-size: 0.875rem;">{{ formatDateTime(value) }}</span>
                  </template>

                  <template #[`item.state`]="{ value }">
                    <div class="d-flex justify-end">
                      <v-chip
                        :color="getStatusColor(value)"
                        size="small"
                        variant="tonal"
                        label
                        class="text-capitalize font-weight-bold"
                      >
                        {{ getStatusLabel(value) }}
                      </v-chip>
                    </div>
                  </template>
                </v-data-table>
              </v-card>

              <!-- Mobile Cards -->
              <div class="d-md-none">
                <v-row dense>
                  <v-col v-for="incident in filteredIncidents" :key="incident.id" cols="12">
                    <v-card
                      rounded="xl"
                      elevation="2"
                      class="mb-3 incident-card"
                      @click="openIncidentDialog(incident)"
                    >
                      <!-- Header with Status Badge -->
                      <div class="pa-4 pb-2 d-flex align-start justify-space-between">
                        <div class="flex-grow-1 pr-3">
                          <div class="d-flex align-center mb-1">
                            <v-chip
                              :color="getStatusColor(incident.state)"
                              size="x-small"
                              variant="flat"
                              class="text-uppercase font-weight-bold mr-2"
                              style="letter-spacing: 0.5px;"
                            >
                              {{ getStatusLabel(incident.state) }}
                            </v-chip>
                            <span class="text-caption" style="color: #9AA6B1;">
                              #{{ incident.serviceNumber || incident.incidentNumber || '-' }}
                            </span>
                          </div>
                          <h3 class="text-body-1 font-weight-semibold mb-1" style="color: #001437; line-height: 1.4;">
                            {{ incident.description }}
                          </h3>
                          <div class="d-flex align-center text-caption" style="color: #9AA6B1;">
                            <v-icon size="12" color="#9AA6B1" class="mr-1">mdi-clock-outline</v-icon>
                            {{ formatDateTime(incident.creationTime) }}
                          </div>
                        </div>
                        
                        <v-avatar 
                          :color="getStatusColor(incident.state)" 
                          size="40" 
                          variant="tonal"
                          class="flex-shrink-0"
                        >
                          <v-icon size="20" :color="getStatusColor(incident.state)">
                            {{ getStatusIcon(incident.state) }}
                          </v-icon>
                        </v-avatar>
                      </div>

                      <v-divider class="mx-4" style="opacity: 0.5;" />

                      <!-- Details Section -->
                      <div class="pa-4 pt-3">
                        <v-row dense class="mb-2">
                          <!-- Category -->
                          <v-col cols="12" class="mb-2">
                            <div class="detail-item">
                              <v-icon size="16" color="#285096" class="mr-2">
                                {{ getCategoryIcon(incident.serviceCategoryName) }}
                              </v-icon>
                              <span class="text-body-2 font-weight-medium" style="color: #285096;">
                                {{ incident.serviceCategoryName || $t('occupancy.all-categories') }}
                              </span>
                            </div>
                          </v-col>

                          <!-- Location -->
                          <v-col cols="12" sm="6" class="mb-2">
                            <div class="detail-item">
                              <v-icon size="14" color="#718096" class="mr-1">mdi-map-marker</v-icon>
                              <span class="text-body-2" style="color: #4E5965;">
                                {{ getLocationDisplay(incident) }}
                              </span>
                            </div>
                          </v-col>

                          <!-- Floor -->
                          <v-col cols="12" sm="6" class="mb-2">
                            <div class="detail-item">
                              <v-icon size="14" color="#718096" class="mr-1">mdi-stairs</v-icon>
                              <span class="text-body-2" style="color: #4E5965;">
                                {{ $t('office.floor') }}: {{ getFloorDisplay(incident) }}
                              </span>
                            </div>
                          </v-col>
                        </v-row>

                        <!-- Action Button -->
                        <v-btn
                          variant="tonal"
                          color="#285096"
                          size="small"
                          rounded="lg"
                          class="text-none mt-2 w-100"
                          prepend-icon="mdi-pencil"
                        >
                          {{ $t('navigation-bar.edit') }} {{ $t('incidents.title').slice(0, -1) }}
                        </v-btn>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-card>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="600" scrollable persistent>
      <v-card rounded="xl">
        <v-card-title class="d-flex align-center pa-4" style="background:#EDF3FD; color:#001437; font-weight:600;">
          <span>{{ editForm.originalData ? $t('incidents.incident-details') : $t('incidents.create-incident') }}</span>
          <v-spacer />
          <v-btn icon variant="text" density="comfortable" :disabled="isSaving" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <v-form ref="editFormRef">
            <v-text-field
              v-model="editForm.location.locationName"
              :label="$t('incidents.incident-location')"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              readonly
              prepend-inner-icon="mdi-map-marker"
              append-inner-icon="mdi-crosshairs-gps"
              color="#23A5E6"
              class="mb-4"
              @click="openMapView"
              @click:append-inner="openMapView"
            />

            <v-select
              v-model="editForm.serviceCategory.id"
              :items="categories"
              item-title="name"
              item-value="id"
              :label="$t('incidents.incident-category')"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              color="#23A5E6"
              class="mb-4"
            >
              <template #prepend-inner>
                <v-icon size="20" color="#285096">{{ getCategoryIcon(getCategoryNameById(editForm.serviceCategory.id)) }}</v-icon>
              </template>
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <template #prepend>
                    <v-icon size="20" color="#285096">{{ getCategoryIcon(item.raw.name) }}</v-icon>
                  </template>
                </v-list-item>
              </template>
            </v-select>

            <v-textarea
              v-model="editForm.description"
              :label="$t('incidents.description')"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              prepend-inner-icon="mdi-text"
              color="#23A5E6"
              rows="3"
              auto-grow
              class="mb-4"
            />

            <p class="text-body-2 mb-2" style="color: #4E5965;">{{ $t('incidents.incident-status') }}</p>
            <v-btn-toggle v-model="editForm.state" mandatory divided rounded="lg" density="comfortable" class="w-100 mb-4">
              <v-btn value="open" :color="editForm.state === 'open' || editForm.state === 'pending' ? '#DC3545' : undefined" :variant="editForm.state === 'open' || editForm.state === 'pending' ? 'flat' : 'outlined'" class="text-none flex-1-1">
                <v-icon start size="18">mdi-alert-circle</v-icon>
                {{ $t('incidents.incident-open') }}
              </v-btn>
              <v-btn value="done" :color="editForm.state === 'done' ? '#28A745' : undefined" :variant="editForm.state === 'done' ? 'flat' : 'outlined'" class="text-none flex-1-1">
                <v-icon start size="18">mdi-check-circle</v-icon>
                {{ $t('office.done') }}
              </v-btn>
            </v-btn-toggle>

            <!-- Image Upload (Premium Feature - Disabled) -->
            <div class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-chip
                  color="#DC3545"
                  size="x-small"
                  variant="flat"
                  prepend-icon="mdi-star"
                  class="text-uppercase font-weight-bold mr-2"
                  style="letter-spacing: 0.5px;"
                >
                  Premium
                </v-chip>
                <span class="text-body-2 font-weight-medium" style="color: #4E5965;">
                  {{ $t('incidents.upload-image') }}
                </span>
              </div>

              <v-file-input
                v-model="editForm.newImages"
                :label="$t('incidents.upload-image')"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                prepend-inner-icon="mdi-camera"
                multiple
                accept="image/png, image/jpeg"
                show-size
                counter
                color="#9AA6B1"
                disabled
                readonly
                class="premium-disabled-input"
              >
                <template #prepend-inner>
                  <v-icon color="#9AA6B1">mdi-camera-off</v-icon>
                </template>
              </v-file-input>

              <p class="text-caption mt-2" style="color: #9AA6B1;">
                <v-icon size="14" color="#9AA6B1" class="mr-1">mdi-information-outline</v-icon>
                Image upload is a premium feature. Contact your administrator to enable this feature.
              </p>
            </div>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4" style="background:#F8FAFC;">
          <v-btn variant="text" color="#4E5965" rounded="lg" class="text-none" :disabled="isSaving" @click="closeDialog">
            {{ $t('incidents.cancel') }}
          </v-btn>

          <v-spacer />

          <v-btn variant="tonal" color="#DC3545" rounded="lg" class="text-none" :loading="isDeleting" :disabled="isSaving" @click="confirmDeleteIncident">
            <v-icon start size="18">mdi-delete</v-icon>
            {{ $t('incidents.delete') }}
          </v-btn>

          <v-btn
            variant="flat"
            color="#285096"
            rounded="lg"
            class="text-none"
            :loading="isSaving"
            :disabled="!isFormModified || isSaving"
            @click="saveIncident"
          >
            <v-icon start size="18">mdi-content-save</v-icon>
            {{ $t('incidents.update') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card rounded="xl" class="text-center pa-4">
        <v-avatar color="#DC3545" size="64" variant="tonal" class="mb-4">
          <v-icon size="32" color="#DC3545">mdi-delete-alert</v-icon>
        </v-avatar>
        <v-card-title class="text-h6" style="color:#001437;">
          {{ $t('incidents.delete-incident') }}
        </v-card-title>
        <v-card-text style="color:#4E5965;">
          {{ $t('incidents.delete-incident-message') }}
        </v-card-text>
        <v-card-actions class="justify-center ga-2 pb-2">
          <v-btn variant="outlined" color="#4E5965" rounded="lg" min-width="100" class="text-none" @click="deleteDialog = false">
            {{ $t('incidents.cancel') }}
          </v-btn>
          <v-btn variant="flat" color="#DC3545" rounded="lg" min-width="100" class="text-none" :loading="isDeleting" @click="deleteIncident">
            {{ $t('incidents.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <MapView ref="mapViewRef" :dragMarker="true" @onPositionSelected="handleLocationSelected" />

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      rounded="lg"
      location="bottom right"
    >
      <div class="d-flex align-center ga-2">
        <v-icon size="20">{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template #actions>
        <v-btn variant="text" class="text-none" @click="snackbar.show = false">
          {{ $t('booking.close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import MapView from '@/components/general/IncidentsMapView.vue'
import Loader from '@/components/general/Loader.vue'
import { getStoredItem } from '@/services/utils.js'
import {
  getAllIncidents,
  getIncidentCategories,
  getFloors,
  getLocations,
  getIncidentById,
  updateIncidentById,
  getBuildings,
  deleteIncidentsFull,
  deleteIncidentById,
  moveIncidentsToCategory,
  getPoisByCategoryId,
  getPoiCategories,
  linkIncidentToCategory,
  getServiceProviderByCategoryId,
  sendInProgress,
  sendIncident,
  closeIncident
} from '@/controllers/incidentsAxiosBypasser'
import { isStaingOrQa, postMultipleFiles, linkIncidentToImage } from '@/controllers/BaseController'

export default {
  name: 'AdminIncidentsManagement',
  components: { MapView, Loader },
  setup() {
    const router = useRouter()
    const { mobile } = useDisplay()
    const { t } = useI18n()

    const mapViewRef = ref(null)
    const editFormRef = ref(null)

    // Loading states
    const isLoading = ref(true)
    const isSaving = ref(false)
    const isDeleting = ref(false)
    const isRefreshing = ref(false)
    const isOpeningIncident = ref(false)
    const isOpeningMap = ref(false)
    
    const showLoader = computed(() => 
      isLoading.value || 
      isRefreshing.value ||
      isOpeningIncident.value || 
      isOpeningMap.value ||
      isSaving.value || 
      isDeleting.value
    )

    const errorMessage = ref(null)
    const searchQuery = ref('')
    const showFilters = ref(false)
    const editDialog = ref(false)
    const deleteDialog = ref(false)
    const BUILDINGS = ref([])
    const incidents = ref([])
    const categories = ref([])
    const floors = ref([])
    const locations = ref([])
    const selectedBuilding = ref(null)

    const snackbar = reactive({
      show: false,
      message: '',
      color: 'success',
      icon: 'mdi-check-circle'
    })

    const filters = reactive({
      timeRange: 'all',
      category: 0,
      floor: 0,
      status: 'all'
    })

    // Category icon mapping
    const categoryIconMap = {
      'Sonstiges - (Other)': 'mdi-dots-horizontal-circle',
      'Sicherheit - (Security)': 'mdi-shield-lock',
      'Gesundheit und Sicherheit - (Health and Safety)': 'mdi-heart-pulse',
      'Wartung - (Maintenance)': 'mdi-tools',
      'IT-Probleme - (IT Problem)': 'mdi-monitor-cellphone',
      'Elektrisch - (Electrical)': 'mdi-lightning-bolt',
      'Reinigung - (Cleaning)': 'mdi-broom',
      'Umwelt - (Environmental)': 'mdi-leaf'
    }

    const getCategoryIcon = (categoryName) => {
      if (!categoryName) return 'mdi-tag-outline'
      return categoryIconMap[categoryName] || 'mdi-tag-outline'
    }

    const getCategoryNameById = (categoryId) => {
      const category = categories.value.find(c => c.id === categoryId)
      return category?.name || ''
    }

    const timeRangeOptions = computed(() => [
      { label: t('booking.all'), value: 'all' },
      { label: t('booking.today'), value: 'today' },
      { label: 'Past', value: 'past' },
      { label: t('booking.current-week'), value: 'week' },
      { label: t('booking.current-month'), value: 'month' }
    ])

    const statusFilterOptions = computed(() => [
      { label: t('booking.all'), value: 'all' },
      { label: t('incidents.incident-open'), value: 'open' },
      { label: t('office.done'), value: 'done' }
    ])

    const currentSort = ref('newest')
    const sortOptions = computed(() => [
      { label: t('navigation-bar.sort') + ' (Newest)', value: 'newest' },
      { label: t('navigation-bar.sort') + ' (Oldest)', value: 'oldest' },
      { label: t('incidents.incident-status'), value: 'status' },
      { label: t('incidents.incident-category'), value: 'category' },
      { label: t('office.location'), value: 'location' }
    ])

    const editForm = reactive({
      id: null,
      version: null,
      tags: {},
      files: { type: 'files', entities: [] },
      customerId: null,
      building: null,
      campus: null,
      serviceCategory: { type: 'servicecategories', id: null },
      incidentCategory: { type: 'incidentcategories', id: null },
      location: {
        locationName: '',
        floorId: null,
        latitude: null,
        longitude: null
      },
      creationTime: null,
      description: '',
      state: 'open',
      creatorName: '',
      creatorEmail: '',
      serviceNumber: '',
      serviceCategoryName: '',
      incidentCategoryName: '',
      buildingName: null,
      floorName: null,
      serviceOrder: null,
      type: 'incidents',
      newImages: [],
      originalData: null
    })

    const tableHeaders = computed(() => [
      { title: 'ID', key: 'incidentNumber', width: '110' },
      { title: t('incidents.description'), key: 'description', minWidth: '180' },
      { title: t('office.location'), key: 'location', minWidth: '150' },
      { title: t('office.floor'), key: 'floorName', width: '80' },
      { title: t('incidents.incident-category'), key: 'serviceCategoryName', minWidth: '160' },
      { title: t('incidents.incident-date'), key: 'creationTime', width: '120' },
      { title: t('incidents.incident-status'), key: 'state', width: '120', align: 'end' }
    ])

    const categoryOptions = computed(() => [
      { id: 0, name: t('occupancy.all-categories') },
      ...categories.value
    ])

    const floorOptions = computed(() => [
      { id: 0, name: t('occupancy.all-floors') },
      ...floors.value
    ])

    const currentSortLabel = computed(() => {
      const option = sortOptions.value.find(o => o.value === currentSort.value)
      return option?.label || t('navigation-bar.sort')
    })

    const hasActiveFilters = computed(() =>
      filters.timeRange !== 'all' ||
      filters.category !== 0 ||
      filters.floor !== 0 ||
      filters.status !== 'all'
    )

    const activeFilterCount = computed(() => {
      let count = 0
      if (filters.timeRange !== 'all') count++
      if (filters.category !== 0) count++
      if (filters.floor !== 0) count++
      if (filters.status !== 'all') count++
      return count
    })

    const getFloorIdFromIncident = (incident) => {
      if (incident.location?.floorId) return incident.location.floorId
      
      if (incident.location?.locationName) {
        let floorName = incident.location?.locationName.toLowerCase()?.split(': ')[1]
        let floor = floors.value.find(f => f.name.toLowerCase() === floorName)
        if (floor) return floor.id
      }
      
      return null
    }

    const getFloorDisplay = (incident) => {
      const floorId = getFloorIdFromIncident(incident)
      if (!floorId) return '-'
      
      const floor = floors.value.find(f => f.id === floorId)
      return floor ? floor?.number : '-'
    }

    const getLocationDisplay = (incident) => {
      return incident.location?.locationName?.split(',')[0] || 'Unknown'
    }

    const filteredIncidents = computed(() => {
      let result = [...incidents.value]

      if (searchQuery.value?.trim()) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(inc => {
          const number = inc.serviceNumber || inc.incidentNumber || ''
          return (
            inc.description?.toLowerCase().includes(query) ||
            getLocationDisplay(inc).toLowerCase().includes(query) ||
            inc.serviceCategoryName?.toLowerCase().includes(query) ||
            String(number).toLowerCase().includes(query) ||
            inc.state?.toLowerCase().includes(query) ||
            getFloorDisplay(inc).toLowerCase().includes(query)
          )
        })
      }

      if (filters.timeRange !== 'all') {
        const now = moment()
        const today = moment().startOf('day')
        result = result.filter(inc => {
          const date = moment(inc.creationTime)
          switch (filters.timeRange) {
            case 'past': return date.isBefore(today)
            case 'today': return date.isSame(now, 'day')
            case 'week': return date.isSame(now, 'week')
            case 'month': return date.isSame(now, 'month')
            default: return true
          }
        })
      }

      if (filters.category !== 0) {
        result = result.filter(inc => {
          const catId = inc.incidentCategory?.id
          return catId === filters.category
        })
      }

      if (filters.floor !== 0) {
        result = result.filter(inc => {
          const floorId = getFloorIdFromIncident(inc)
          return floorId === filters.floor
        })
      }

      if (filters.status !== 'all') {
        result = result.filter(inc => {
          // Treat pending as open in filter
          if (filters.status === 'open') {
            return inc.state === 'open' || inc.state === 'pending'
          }
          return inc.state === filters.status
        })
      }

      result.sort((a, b) => {
        switch (currentSort.value) {
          case 'newest': return new Date(b.creationTime) - new Date(a.creationTime)
          case 'oldest': return new Date(a.creationTime) - new Date(b.creationTime)
          case 'status': {
            const order = { open: 0, pending: 1, done: 2 }
            return (order[a.state] || 0) - (order[b.state] || 0)
          }
          case 'category':
            return (a.serviceCategoryName || '').localeCompare(b.serviceCategoryName || '')
          case 'location':
            return getLocationDisplay(a).localeCompare(getLocationDisplay(b))
          default: return 0
        }
      })

      return result
    })

    const statusSummary = computed(() => {
      const summary = { open: 0, done: 0 }
      incidents.value.forEach(i => {
        // Treat pending as open in the UI
        if (i.state === 'open' || i.state === 'pending') {
          summary.open++
        } else if (i.state === 'done') {
          summary.done++
        }
      })
      return summary
    })

    const isFormModified = computed(() => {
      if (!editForm.originalData) return false
      const orig = editForm.originalData
      
      return (
        editForm.location.locationName !== orig.location.locationName ||
        editForm.location.latitude !== orig.location.latitude ||
        editForm.location.longitude !== orig.location.longitude ||
        editForm.location.floorId !== orig.location.floorId ||
        editForm.serviceCategory.id !== orig.serviceCategory.id ||
        editForm.description !== orig.description ||
        editForm.state !== orig.state ||
        (editForm.newImages?.length || 0) > 0
      )
    })

    const showNotification = (message, type = 'success') => {
      snackbar.message = message
      snackbar.color = type === 'success' ? '#28A745' : type === 'error' ? '#DC3545' : '#23A5E6'
      snackbar.icon = type === 'success' ? 'mdi-check-circle' : type === 'error' ? 'mdi-alert-circle' : 'mdi-information'
      snackbar.show = true
    }

    const logError = (context, err) => {
      console.error(`[AdminIncidents] ${context}:`, err?.message || err)
    }

    const goBack = () => router.go(-1)

    const formatDateTime = dateStr => !dateStr ? '' : moment(dateStr).format('DD MMM, HH:mm')
    const truncateText = (text, maxLen) => !text ? '' : text.length > maxLen ? text.substring(0, maxLen) + '...' : text

    const getStatusColor = status => {
      // Treat pending as open in UI
      if (status === 'open' || status === 'pending') return '#DC3545'
      if (status === 'done') return '#28A745'
      return '#9AA6B1'
    }

    const getStatusLabel = status => {
      // Treat pending as open in UI
      if (status === 'open' || status === 'pending') return t('incidents.incident-open')
      if (status === 'done') return t('office.done')
      return status
    }

    const getStatusIcon = status => {
      // Treat pending as open in UI
      if (status === 'open' || status === 'pending') return 'mdi-alert-circle'
      if (status === 'done') return 'mdi-check-circle'
      return 'mdi-help-circle'
    }

    const clearFilters = () => {
      filters.timeRange = 'all'
      filters.category = 0
      filters.floor = 0
      filters.status = 'all'
    }

    const fetchInitialData = async () => {
      isLoading.value = true
      errorMessage.value = null
      try {
        const [
          incidentsList,
          isPIA,
          categoryList,
          floorList,
          buildings,
          locationList
        ] = await Promise.all([
          getAllIncidents(),
          isStaingOrQa(),
          getIncidentCategories(),
          getFloors(),
          getBuildings(),
          getLocations()
        ])

        const buildingName = isPIA
          ? buildings.find(b => b.name === 'Teichhaus')?.name
          : getStoredItem('building')
        selectedBuilding.value = buildings.find(b => b.name === buildingName)
        BUILDINGS.value = buildings
        
        // Normalize incident data from API
        incidentsList.forEach((incident) => {
          // Use location object directly from API
          if (!incident.location || !incident.location.locationName) {
            incident.location = {
              locationName: 'Unknown',
              floorId: null,
              latitude: null,
              longitude: null
            }
          }
          
          // Use state from API
          if (!incident.state) {
            incident.state = 'open'
          }
          
          // Ensure category name is available
          if (!incident.serviceCategoryName && incident.incidentCategoryName) {
            incident.serviceCategoryName = incident.incidentCategoryName
          }
        })
        
        incidents.value = incidentsList || []
        categories.value = categoryList.filter(category => category?.campusId === selectedBuilding.value?.campus?.id) || []
        floors.value = floorList.filter(floor => floor?.pois?.ids?.length > 0 && floor?.building?.id === selectedBuilding.value?.id) || []
        locations.value = locationList || []
      } catch (err) {
        logError('fetchInitialData', err)
        errorMessage.value = t('incidents.loading-error')
      } finally {
        isLoading.value = false
      }
    }

    const refreshData = async () => {
      if (isRefreshing.value) return
      isRefreshing.value = true
      try {
        await deleteIncidentsFull()
        await fetchInitialData()
        showNotification(t('incidents.refreshing'))
      } catch (err) {
        logError('refreshData', err)
        showNotification(t('incidents.refresh-failed'), 'error')
      } finally {
        isRefreshing.value = false
      }
    }

    const openIncidentDialog = async incident => {
      isOpeningIncident.value = true
      try {
        const inc = await getIncidentById(incident.id)
        
        // Map correct API fields
        const categoryId = inc.incidentCategory?.id || null
        const categoryName = inc.incidentCategoryName || inc.serviceCategoryName || ''
        
        Object.assign(editForm, {
          id: inc.id,
          version: inc.version,
          tags: inc.tags || {},
          files: inc.files || { type: 'files', entities: [] },
          customerId: inc.customerId,
          building: inc.building,
          campus: inc.campus,
          serviceCategory: { type: 'servicecategories', id: categoryId },
          incidentCategory: { type: 'incidentcategories', id: categoryId },
          location: {
            locationName: inc.location?.locationName || '',
            floorId: inc.location?.floorId || null,
            latitude: inc.location?.latitude || null,
            longitude: inc.location?.longitude || null
          },
          creationTime: inc.creationTime,
          description: inc.description || '',
          state: inc.state || 'open',
          creatorName: inc.creatorName || '',
          creatorEmail: inc.creatorEmail || '',
          serviceNumber: inc.incidentNumber || inc.serviceNumber || '',
          serviceCategoryName: categoryName,
          incidentCategoryName: categoryName,
          buildingName: inc.buildingName,
          floorName: inc.floorName,
          serviceOrder: inc.serviceOrder,
          type: 'incidents',
          newImages: []
        })

        editForm.originalData = JSON.parse(JSON.stringify({
          location: editForm.location,
          serviceCategory: editForm.serviceCategory,
          incidentCategory: editForm.incidentCategory,
          description: editForm.description,
          state: editForm.state
        }))

        editDialog.value = true
      } catch (err) {
        logError('openIncidentDialog', err)
        showNotification(t('incidents.loading-error'), 'error')
      } finally {
        isOpeningIncident.value = false
      }
    }

    const closeDialog = () => { 
      if (!isSaving.value) {
        editDialog.value = false
        editForm.newImages = []
      }
    }

    const openMapView = async () => {
      isOpeningMap.value = true
      try {
        const poiCategories = await getPoiCategories()
        let allPois = []
        for (const cat of poiCategories) {
          try {
            const pois = await getPoisByCategoryId(cat.id)
            allPois = allPois.concat(pois)
          } catch (err) { logError('loadPois', err) }
        }

        if (mapViewRef.value) {
          if (editForm.location.latitude && editForm.location.longitude) {
            mapViewRef.value.openMap(
              selectedBuilding.value, 
              allPois, 
              poiCategories, 
              { 
                lat: editForm.location.latitude, 
                long: editForm.location.longitude 
              }
            )
          } else {
            mapViewRef.value.openMap(selectedBuilding.value, allPois, poiCategories)
          }
        }
      } catch (err) {
        logError('openMapView', err)
        showNotification(t('incidents.error-incident'), 'error')
      } finally {
        isOpeningMap.value = false
      }
    }

    const handleLocationSelected = ({ coordinates, floor, locationName }) => {
      if (!coordinates) return
      editForm.location.latitude = coordinates[0]
      editForm.location.longitude = coordinates[1]
      editForm.location.locationName = locationName || ''
      editForm.location.floorId = floor?.id || null
      editForm.floorName = floor?.name || null
    }

    const getImagePreview = file => file instanceof File ? URL.createObjectURL(file) : ''
    const removeNewImage = index => editForm.newImages.splice(index, 1)

    const resizeImage = (file, maxW = 750, maxH = 498) => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.src = URL.createObjectURL(file)
        img.onload = () => {
          const canvas = document.createElement('canvas')
          let w = img.width, h = img.height
          if (w > maxW) { h *= maxW / w; w = maxW }
          if (h > maxH) { w *= maxH / h; h = maxH }
          canvas.width = w; canvas.height = h
          canvas.getContext('2d').drawImage(img, 0, 0, w, h)
          canvas.toBlob(blob => {
            URL.revokeObjectURL(img.src)
            blob ? resolve(new File([blob], file.name, { type: file.type })) : reject(new Error('Resize failed'))
          }, 'image/png')
        }
        img.onerror = () => { URL.revokeObjectURL(img.src); reject(new Error('Load failed')) }
      })
    }

    const confirmDeleteIncident = () => { deleteDialog.value = true }

    const deleteIncident = async () => {
      if (!editForm.id) return
      isDeleting.value = true
      try {
        await deleteIncidentById(editForm.id)
        deleteDialog.value = false
        editDialog.value = false
        showNotification(t('incidents.deleted-incident'))
        await fetchInitialData()
      } catch (err) {
        logError('deleteIncident', err)
        showNotification(t('incidents.delete-incident-failed'), 'error')
      } finally {
        isDeleting.value = false
      }
    }

    const saveIncident = async () => {
      if (!isFormModified.value || isSaving.value) return
      isSaving.value = true
      
      try {
        console.log('=== SAVE INCIDENT START ===')
        
        const latest = await getIncidentById(editForm.id)
        const incidentBuildingName = BUILDINGS.value.find(b => b.id === editForm.building?.id)?.name || null
        
        const updatePayload = {
          id: latest.id,
          version: latest.version,
          tags: latest.tags || {},
          files: latest.files || { type: 'files', entities: [] },
          building: editForm.building,
          campus: editForm.campus,
          incidentCategory: {
            type: 'incidentcategories',
            id: editForm.serviceCategory.id
          },
          location: {
            locationName: editForm.location.locationName,
            floorId: editForm.location.floorId,
            latitude: editForm.location.latitude,
            longitude: editForm.location.longitude
          },
          creationTime: latest.creationTime,
          description: editForm.description,
          state: editForm.state,
          creatorName: latest.creatorName || '',
          creatorEmail: latest.creatorEmail || '',
          incidentNumber: latest.incidentNumber,
          serviceOrder: latest.serviceOrder,
          incidentCategoryName: editForm.incidentCategoryName,
          buildingName: incidentBuildingName,
          floorName: editForm.floorName,
          type: 'incidents'
        }

        await updateIncidentById(editForm.id, updatePayload)
        console.log('✓ Incident updated')

        const originalCategoryId = editForm.originalData.serviceCategory.id
        const newCategoryId = editForm.serviceCategory.id
        
        if (originalCategoryId !== newCategoryId) {
          console.log('Category changed, linking...')
          await linkIncidentToCategory(editForm.id, newCategoryId)
          console.log('✓ Category linked')
        }

        const originalState = editForm.originalData.state
        const newState = editForm.state
        
        if (originalState !== newState && newState === 'done') {
          console.log(`\n=== CLOSING INCIDENT: ${originalState} → ${newState} ===`)
          
          const freshIncident = await getIncidentById(editForm.id)
          const serviceOrderId = freshIncident.serviceOrder?.id
          
          if (!serviceOrderId) {
            throw new Error('No service order found for this incident')
          }

          const serviceProviders = await getServiceProviderByCategoryId(newCategoryId)
          if (!serviceProviders?.length) {
            throw new Error(`No service provider assigned to category: ${editForm.incidentCategoryName}`)
          }

          const serviceProviderId = serviceProviders[0].id
          console.log(`Service Provider: ${serviceProviders[0].name} (${serviceProviderId})`)

          console.log('Step 3: Attaching service provider...')
          await sendInProgress(serviceOrderId, serviceProviderId)
          console.log('✓ Service provider attached')

          console.log('Step 4: Sending notification...')
          await sendIncident(serviceOrderId)
          console.log('✓ Notification sent')

          console.log('Step 5: Closing incident...')
          await closeIncident(serviceOrderId)
          console.log('✓ Incident closed')

          console.log('=== WORKFLOW COMPLETE ===\n')
        }

        editDialog.value = false
        showNotification(t('incidents.updated-incident'))
        refreshData()
        
      } catch (err) {
        logError('saveIncident', err)
        const errorMsg = err.message || 'Unknown error'
        showNotification(`${t('incidents.failed-incident')}: ${errorMsg}`, 'error')
        console.error('❌ Error:', err)
      } finally {
        isSaving.value = false
      }
    }

    onMounted(async () => {
      showFilters.value = !mobile.value
      await fetchInitialData()
    })

    return {
      mapViewRef, editFormRef, isLoading, isSaving, isDeleting, isRefreshing,
      isOpeningIncident, isOpeningMap, showLoader, errorMessage,
      searchQuery, showFilters, editDialog, deleteDialog, snackbar,
      incidents, categories, floors, locations, filters, editForm,
      timeRangeOptions, statusFilterOptions, sortOptions, currentSort,
      tableHeaders, categoryOptions, floorOptions, currentSortLabel,
      hasActiveFilters, activeFilterCount, filteredIncidents, isFormModified,
      statusSummary, goBack, formatDateTime, truncateText, getStatusColor,
      getStatusLabel, getStatusIcon, clearFilters, refreshData,
      openIncidentDialog, closeDialog, openMapView, handleLocationSelected,
      getImagePreview, removeNewImage, confirmDeleteIncident, deleteIncident, 
      saveIncident, getFloorDisplay, getLocationDisplay, BUILDINGS,
      getCategoryIcon, getCategoryNameById
    }
  }
}
</script>

<style scoped>
.responsive-table :deep(.v-data-table__td) {
  white-space: normal !important;
  vertical-align: top !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.responsive-table :deep(.v-data-table__th) {
  white-space: nowrap !important;
}

.responsive-table :deep(table) {
  table-layout: auto !important;
}

/* Premium Info Card */
.premium-info-card {
  background: linear-gradient(135deg, #FFF5F7 0%, #FFFFFF 100%);
  border: 1px solid #FFE5E5;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.premium-info-card:hover {
  border-color: #FFD1D1;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.1);
}

/* Mobile Card Styles */
.incident-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
}

.incident-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.12) !important;
  border-color: rgba(40, 80, 150, 0.2);
}

.incident-card:active {
  transform: translateY(0px);
  transition: all 0.1s;
}

.detail-item {
  display: flex;
  align-items: center;
  min-height: 24px;
}

/* Status chip styling */
.incident-card :deep(.v-chip) {
  font-size: 0.65rem;
  height: 20px;
  padding: 0 8px;
}

/* Smooth transitions for all interactive elements */
.incident-card * {
  transition: color 0.2s ease;
}

/* Premium Disabled Input Styling */
.premium-disabled-input :deep(.v-input__control) {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.premium-disabled-input :deep(.v-field) {
  background-color: #F7FAFC !important;
  border-color: #E2E8F0 !important;
}

.premium-disabled-input :deep(.v-field__input) {
  color: #9AA6B1 !important;
}

.premium-disabled-input :deep(.v-icon) {
  opacity: 0.5;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .premium-info-card {
    padding: 16px !important;
  }

  .premium-info-card .text-subtitle-2 {
    font-size: 0.875rem;
  }

  .premium-info-card .text-body-2 {
    font-size: 0.8125rem;
  }
}
</style>
